import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import {
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessageType,
  SetReactionMessage,
} from 'messages/websocket.messages';

import { WebsocketService } from '../websocket.service';

export const EMOTE_DURATION = 5000;

@Injectable({
  providedIn: 'root',
})
export class EmoteService extends Loggable {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  private emoteStreamSource = new BehaviorSubject<UserEmote>({ timestamp: Date.now() });
  public emoteStream$ = this.emoteStreamSource.asObservable();
  private userId?: string;
  private clearTimeout?: NodeJS.Timeout;
  public emooteLength = EMOTE_DURATION;

  public constructor(
    private websocketService: WebsocketService,
    private store: Store,
  ) {
    super();
    this.setDisplayName('EmoteService');
    this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleWebsocketMessage);

    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userId) => (this.userId = userId));
  }

  public setReactionMessage(emote: Emoticon): void {
    try {
      const msg: SetReactionMessage = {
        t: OutgoingMessageType.SET_REACTION,
        reaction: JSON.stringify(emote),
      };
      this.websocketService.sendMessage(msg);
      this.emoteStreamSource.next({ userId: this.userId, emote, timestamp: Date.now() });
    } catch (e) {
      this.error('Error sending reaction', e);
    }
  }

  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.REACTION: {
        if (msg.reaction) {
          if (this.clearTimeout) {
            clearTimeout(this.clearTimeout);
          }
          const emote: Emoticon = JSON.parse(msg.reaction);
          this.emoteStreamSource.next({ userId: msg.userId, emote, timestamp: Date.now() });
          // clear emoji after 5 seconds
          this.clearTimeout = setTimeout(() => {
            this.emoteStreamSource.next({ timestamp: Date.now() });
          }, this.emooteLength);
        }
        return;
      }
    }
  };
}

export interface Emoticon {
  id: string;
  name: string;
  colons: string;
  text: string;
  emoticons: string[];
  skin: null | string;
  native: string;
}

export interface UserEmote {
  userId?: string;
  emote?: Emoticon;
  timestamp: number;
}
export const DefaultEmojis = [
  'smile',
  'heart',
  'clap',
  'thumbsup',
  'thumbsdown',
  'laugh',
  'cry',
  'angry',
  'fire',
  'rocket',
];
